<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-gray-900">إدارة أوقات الجلسات</h2>
      <div class="flex gap-3">
        <button 
          @click="manualPrayerTimeUpdate"
          :disabled="updatingPrayerTimes"
          class="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <svg v-if="!updatingPrayerTimes" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <div v-else class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          {{ updatingPrayerTimes ? 'جاري التحديث...' : 'تحديث أوقات الصلاة' }}
        </button>
        <button 
          @click="openCreateModal"
          class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          إضافة جلسة جديدة
        </button>
      </div>
    </div>

    <!-- Region Selector -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-2">المنطقة</label>
      <select 
        v-model="selectedRegionId" 
        @change="loadSessionTimes"
        class="w-64 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
      >
        <option value="">اختر المنطقة</option>
        <option v-for="region in regions" :key="region.id" :value="region.id">
          {{ region.regionNameAr }}
        </option>
      </select>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p class="mt-2 text-gray-600">جاري تحميل أوقات الجلسات...</p>
    </div>

    <!-- Session Times Table -->
    <div v-else-if="sessionTimes.length > 0" class="bg-white shadow-lg rounded-lg overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ترتيب الجلسة</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الصلاة</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت الأذان</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت البداية</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت النهاية</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عدد الدارسين</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="session in sessionTimes" :key="session.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              <div class="flex items-center gap-2">
                <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-bold">
                  {{ session.sessionOrder || 'N/A' }}
                </span>
                <div class="flex flex-col gap-1">
                  <button 
                    @click="moveSlotUp(session)"
                    :disabled="!canMoveUp(session)"
                    class="w-6 h-6 flex items-center justify-center text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                    :title="canMoveUp(session) ? 'نقل الجلسة لأعلى' : 'لا يمكن النقل لأعلى'"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                    </svg>
                  </button>
                  <button 
                    @click="moveSlotDown(session)"
                    :disabled="!canMoveDown(session)"
                    class="w-6 h-6 flex items-center justify-center text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                    :title="canMoveDown(session) ? 'نقل الجلسة لأسفل' : 'لا يمكن النقل لأسفل'"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ getPrayerNameArabic(session.prayerName) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ formatTime(session.prayerTime) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ formatTime(session.sessionStartTime) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ formatTime(session.sessionEndTime) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <button 
                v-if="session.currentStudents > 0"
                @click="openStudentModal(session)"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                {{ session.currentStudents }} دارس
              </button>
              <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                0 دارس
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <div class="flex gap-2">
                <button 
                  v-if="session.canDelete"
                  @click="deleteSession(session)"
                  class="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                >
                  حذف
                </button>
                <span v-else class="text-gray-400 text-xs">
                  ({{ session.currentStudents }} دارس مسجل)
                </span>
              </div>
            </td>
          </tr>
        </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="selectedRegionId" class="text-center py-12">
      <div class="mx-auto h-12 w-12 text-gray-400">
        <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 8v24M6 18v6M18 18v6M36 8v24M30 18v6M42 18v6"/>
        </svg>
      </div>
      <h3 class="mt-2 text-sm font-medium text-gray-900">لا توجد أوقات جلسات</h3>
      <p class="mt-1 text-sm text-gray-500">ابدأ بإضافة جلسة جديدة</p>
    </div>

    <!-- Create Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-96 max-w-md mx-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">إضافة جلسة جديدة</h3>

        <form @submit.prevent="saveSession" class="space-y-4">
          <!-- Prayer Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">الصلاة</label>
            <select 
              v-model="formData.athanTimeId" 
              @change="onPrayerChange"
              required
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">اختر الصلاة</option>
              <option v-for="athan in athanTimes" :key="athan.id" :value="athan.id">
                {{ getPrayerNameArabic(athan.prayerName) }} - {{ formatTime(athan.prayerTime) }}
              </option>
            </select>
          </div>

          <!-- Available Time Slots Dropdown (shown after prayer selection) -->
          <div v-if="availableTimeSlots.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">الوقت المتاح</label>
            
            <!-- Prayer Boundary Info -->
            <div class="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p class="text-xs text-blue-700">
                <strong>حدود الصلاة:</strong> 
                {{ formatTime(prayerBoundaryStart) }} - {{ formatTime(prayerBoundaryEnd) }}
                <br>
                <span class="text-blue-600">(الجلسات يجب أن تنتهي قبل الصلاة التالية)</span>
              </p>
            </div>
            
            <select 
              v-model="formData.selectedTimeSlot"
              @change="onTimeSlotChange"
              required
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">اختر الوقت المتاح</option>
              <option v-for="slot in availableTimeSlots" :key="slot.startTime" :value="slot">
                {{ formatTime(slot.startTime) }} - {{ formatTime(slot.endTime) }}
              </option>
            </select>
            
            <!-- Show selected time slot description -->
            <div v-if="formData.selectedTimeSlot" class="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-700">
                <strong>الوقت المختار:</strong> {{ formatTime(formData.selectedTimeSlot.startTime) }} - {{ formatTime(formData.selectedTimeSlot.endTime) }}
              </p>
              <p class="text-xs text-green-600 mt-1">
                {{ formData.selectedTimeSlot.description }}
              </p>
            </div>
            
            <p class="text-xs text-gray-500 mt-1">
              اختر الوقت المناسب للجلسة الجديدة
            </p>
          </div>

          <!-- Manual Time Input (fallback if no slots available) -->
          <div v-if="availableTimeSlots.length === 0 && formData.athanTimeId">
            <div class="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div class="flex items-start">
                <svg class="h-5 w-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                <div class="mr-2">
                  <p class="text-sm text-yellow-700 font-medium">لا توجد أوقات متاحة</p>
                  <p class="text-xs text-yellow-600 mt-1">
                    لا توجد فترات متاحة للصلاة المحددة. يمكنك إدخال الوقت يدوياً.
                  </p>
                </div>
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">وقت البداية</label>
                <input 
                  type="time" 
                  v-model="formData.manualStartTime"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">وقت النهاية</label>
                <input 
                  type="time" 
                  v-model="formData.manualEndTime"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="text-red-600 text-sm">
            {{ errorMessage }}
          </div>

          <!-- Buttons -->
          <div class="flex justify-end gap-3 pt-4">
            <button 
              type="button"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
            >
              إلغاء
            </button>
            <button 
              type="submit"
              :disabled="submitting"
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {{ submitting ? 'جاري الحفظ...' : 'إضافة' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Student Management Modal -->
    <div v-if="showStudentModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <!-- Modal Header -->
          <div class="flex justify-between items-center mb-4">
            <div>
              <h3 class="text-lg font-medium text-gray-900">إدارة الدارسين</h3>
              <p class="text-sm text-gray-600">
                الجلسة: {{ formattedSessionTimeDisplay }} - {{ getPrayerNameArabic(selectedSessionTime?.prayerName) }}
              </p>
            </div>
            <button @click="closeStudentModal" class="text-gray-400 hover:text-gray-600">
              <span class="sr-only">إغلاق</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="loadingStudents" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p class="mt-2 text-gray-600">جاري تحميل الدارسين...</p>
          </div>

          <!-- Students List (Active Students Only) -->
          <div v-else-if="sessionStudents.length > 0" class="space-y-2">
            <div v-for="student in sessionStudents" :key="student.studentId" 
                 class="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg">
              
              <div class="flex items-center gap-3">
                <h4 class="font-medium text-gray-900">{{ student.studentName }}</h4>
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  نشط
                </span>
              </div>
              
              <div class="flex gap-2">
                <select 
                  @change="moveStudent(student, $event.target.value)"
                  class="text-sm border border-blue-300 rounded pr-8 pl-2 py-1 bg-blue-50 text-blue-700 focus:ring-2 focus:ring-blue-500 appearance-none bg-no-repeat bg-right pr-8"
                  style="background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236B7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.1c2.2%202.2%205.3%203.2%208.3%203.2s6.1-1%208.3-3.2L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22/%3E%3C/svg%3E'); background-size: 12px 12px; background-position: right 8px center;"
                >
                  <option value="">نقل إلى...</option>
                  <option v-for="slot in student.availableSlots" :key="slot.id" :value="slot.id">
                    {{ getPrayerNameArabic(slot.prayerName) }} {{ formatTime(slot.sessionStartTime) }}-{{ formatTime(slot.sessionEndTime) }} ({{ slot.currentStudents }}/{{ slot.maxCapacity }} دارس - {{ slot.maxCapacity - slot.currentStudents }} متاح)
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- No Active Students -->
          <div v-else class="text-center py-8 text-gray-500">
            <p>لا توجد دارسين نشطين في هذه الجلسة</p>
          </div>

          <!-- Modal Footer -->
          <div class="flex justify-end mt-6">
            <button 
              @click="closeStudentModal"
              class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
            >
              إغلاق
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" 
         class="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
      {{ successMessage }}
    </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'SessionTimeManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      showModal: false,
      selectedRegionId: '',
      sessionTimes: [],
      regions: [],
      athanTimes: [],
      formData: {
        athanTimeId: '',
        selectedTimeSlot: null,
        manualStartTime: '',
        manualEndTime: ''
      },
      errorMessage: '',
      successMessage: '',
      prayerNames: {
        'FAJR': 'الفجر',
        'DHUHR': 'الظهر', 
        'ASR': 'العصر',
        'MAGHRIB': 'المغرب',
        'ISHA': 'العشاء'
      },
      
      // Student Management Modal
      showStudentModal: false,
      loadingStudents: false,
      selectedSessionTime: null,
      sessionStudents: [],
      availableSlots: [],
      loadingSlots: false,
      updatingPrayerTimes: false,
      availableTimeSlots: [], // New data property for available slots
      prayerBoundaryStart: null, // New data property for prayer boundary start
      prayerBoundaryEnd: null // New data property for prayer boundary end
    };
  },
  
  computed: {
    /**
     * Format session time display consistently with AM/PM format
     */
    formattedSessionTimeDisplay() {
      if (!this.selectedSessionTime) return '';
      
      const startTime = this.formatTime(this.selectedSessionTime.sessionStartTime);
      const endTime = this.formatTime(this.selectedSessionTime.sessionEndTime);
      
      return `${startTime} - ${endTime}`;
    }
  },
  
  async mounted() {
    await this.loadRegions();
  },

  methods: {
    async loadRegions() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_regions,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        this.regions = response.data.result.data;
        if (this.regions.length > 0) {
          this.selectedRegionId = this.regions[0].id;
          await this.loadSessionTimes();
          await this.loadAthanTimes();
        }
      } catch (error) {
        console.error('Error loading regions:', error);
        this.showError('خطأ في تحميل المناطق');
      }
    },

    async loadSessionTimes() {
      if (!this.selectedRegionId) return;
      
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}${url.get_session_times}${this.selectedRegionId}`,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        this.sessionTimes = response.data.result.data;
      } catch (error) {
        console.error('Error loading session times:', error);
        this.showError('خطأ في تحميل أوقات الجلسات');
      } finally {
        this.loading = false;
      }
    },

    async loadAthanTimes() {
      if (!this.selectedRegionId) return;
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}${url.master_athan_time}${this.selectedRegionId}`,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        this.athanTimes = response.data.result.data;
      } catch (error) {
        console.error('Error loading athan times:', error);
      }
    },

    openCreateModal() {
      if (!this.selectedRegionId) {
        this.showError('يرجى اختيار المنطقة أولاً');
        return;
      }
      this.formData = {
        athanTimeId: '',
        selectedTimeSlot: null,
        manualStartTime: '',
        manualEndTime: ''
      };
      this.errorMessage = '';
      this.showModal = true;
      this.availableTimeSlots = []; // Clear available slots when opening modal
    },

    closeModal() {
      this.showModal = false;
      this.errorMessage = '';
    },

    async saveSession() {
      this.submitting = true;
      this.errorMessage = '';
      
      try {
        // Validate form data
        if (!this.formData.athanTimeId) {
          throw new Error('يجب اختيار الصلاة');
        }
        
        if (!this.formData.selectedTimeSlot && (!this.formData.manualStartTime || !this.formData.manualEndTime)) {
          throw new Error('يجب اختيار الوقت المتاح أو إدخال الوقت يدوياً');
        }
        
        // Prepare payload based on input method
        let payload;
        if (this.formData.selectedTimeSlot) {
          // Use selected time slot
          payload = {
            athanTimeId: this.formData.athanTimeId,
            sessionStartTime: this.formData.selectedTimeSlot.startTime,
            sessionEndTime: this.formData.selectedTimeSlot.endTime
          };
        } else {
          // Use manual time input
          payload = {
            athanTimeId: this.formData.athanTimeId,
            sessionStartTime: this.formData.manualStartTime,
            sessionEndTime: this.formData.manualEndTime
          };
        }
        
        // Create the session
        await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.create_session_time,
          payload,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        
        this.showSuccess('تم إنشاء الجلسة بنجاح!');
        this.closeModal();
        await this.loadSessionTimes(); // Reload to show new session
        
      } catch (error) {
        console.error('Error creating session:', error);
        this.errorMessage = error.response?.data?.result?.mesAr || error.message || 'خطأ في إنشاء الجلسة';
      } finally {
        this.submitting = false;
      }
    },

    async deleteSession(session) {
      if (!confirm(`هل أنت متأكد من حذف جلسة ${this.getPrayerNameArabic(session.prayerName)}؟`)) {
        return;
      }

      try {
        await axios.delete(
          `${process.env.VUE_APP_SERVICE_URL}${url.delete_session_time}${session.id}`,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        
        await this.loadSessionTimes();
        this.showSuccess('تم حذف الجلسة بنجاح');
        
      } catch (error) {
        console.error('Error deleting session:', error);
        this.showError(error.response?.data?.result?.mesAr || 'خطأ في حذف الجلسة');
      }
    },

    getPrayerNameArabic(prayerName) {
      return this.prayerNames[prayerName] || prayerName;
    },

    formatTime(timeString) {
      if (!timeString) return '';
      
      // Parse the time string and format it in 12-hour AM/PM format
      const [hours, minutes] = timeString.split(':');
      if (!hours || !minutes) return timeString;
      
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      // Format in Arabic with 12-hour format and AM/PM
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    showSuccess(message) {
      this.successMessage = message;
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    },

    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = '';
      }, 5000);
    },

    // New method to handle prayer selection change
    onPrayerChange() {
      this.availableTimeSlots = []; // Clear available slots when prayer changes
      this.formData.selectedTimeSlot = null; // Clear selected time slot
      this.formData.manualStartTime = ''; // Clear manual time inputs
      this.formData.manualEndTime = '';
      this.prayerBoundaryStart = null; // Clear boundary info
      this.prayerBoundaryEnd = null;
      
      // Calculate available time slots for the selected prayer
      if (this.formData.athanTimeId) {
        this.calculateAvailableTimeSlots();
      }
    },

    // New method to handle time slot selection change
    onTimeSlotChange() {
      // This method is now redundant as the description is shown directly below the dropdown
      // Keeping it for now, but it can be removed if not used elsewhere.
    },
    
    /**
     * Calculate available time slots for the selected prayer
     * This shows admins exactly when they can schedule new sessions
     */
    calculateAvailableTimeSlots() {
      if (!this.formData.athanTimeId) return;
      
      // Find the selected athan time
      const selectedAthan = this.athanTimes.find(athan => athan.id === this.formData.athanTimeId);
      if (!selectedAthan) return;
      
      // Determine prayer order to find the next prayer boundary
      const order = ['FAJR','DHUHR','ASR','MAGHRIB','ISHA'];
      const selectedPrayerName = (selectedAthan.prayerName || '').toUpperCase();
      const selectedIndex = order.indexOf(selectedPrayerName);
      
      // Compute boundary start and end for the selected prayer
      const baseDate = '2000-01-01T';
      const toDate = (t) => new Date(baseDate + t);
      const toTime = (d) => d.toTimeString().substring(0,5);
      
      const prayerTimeDate = toDate(selectedAthan.prayerTime);
      const boundaryStart = new Date(prayerTimeDate.getTime() + (selectedAthan.sessionStartAfter * 60000));
      
      // Next prayer time (if available)
      let boundaryEnd;
      if (selectedIndex >= 0 && selectedIndex < order.length - 1) {
        const nextPrayerName = order[selectedIndex + 1];
        const next = this.athanTimes.find(a => (a.prayerName || '').toUpperCase() === nextPrayerName);
        if (next) {
          // Set boundary to 30 minutes BEFORE the next prayer starts
          // This ensures no session overlaps with the next prayer
          const nextPrayerTime = toDate(next.prayerTime);
          boundaryEnd = new Date(nextPrayerTime.getTime() - (30 * 60000)); // 30 minutes buffer
          
          // Debug logging
          console.log(`Prayer: ${selectedPrayerName}, Next: ${nextPrayerName}`);
          console.log(`Next prayer time: ${toTime(nextPrayerTime)}`);
          console.log(`Boundary end (30 min before): ${toTime(boundaryEnd)}`);
        }
      }
      // Fallbacks if no next prayer (e.g., ISHA) or not found
      if (!boundaryEnd) {
        // Cap to 23:29 for same-day sessions, but ensure 30-minute buffer
        boundaryEnd = new Date(baseDate + '23:29'); // 23:29 instead of 23:59 to allow 30-min session
      }
      
      this.prayerBoundaryStart = toTime(boundaryStart);
      this.prayerBoundaryEnd = toTime(boundaryEnd);
      
      console.log(`Final boundaries for ${selectedPrayerName}: ${toTime(boundaryStart)} - ${toTime(boundaryEnd)}`);

      // Get existing sessions for this prayer
      const existingSessions = this.sessionTimes
        .filter(session => session.athanTimeId === this.formData.athanTimeId)
        .filter(session => {
          // Filter out sessions that overlap with prayer boundaries
          const sessionEnd = toDate(session.sessionEndTime);
          const isValid = sessionEnd <= boundaryEnd;
          
          if (!isValid) {
            console.warn(`Filtering out overlapping session: ${session.sessionStartTime} - ${session.sessionEndTime} (ends after boundary: ${toTime(boundaryEnd)})`);
          }
          
          return isValid;
        })
        .sort((a, b) => toDate(a.sessionStartTime) - toDate(b.sessionStartTime));
      
      console.log(`Valid existing sessions for ${selectedPrayerName}:`, existingSessions.length);

      const availableSlots = [];
      
      if (existingSessions.length === 0) {
        // First possible slot strictly within boundaries
        const firstStart = new Date(Math.max(boundaryStart.getTime(), toDate(selectedAthan.prayerTime).getTime() + selectedAthan.sessionStartAfter * 60000));
        const firstEnd = new Date(firstStart.getTime() + 30 * 60000);
        
        // Validate that the slot fits within boundaries
        if (firstEnd <= boundaryEnd) {
          availableSlots.push({
            startTime: toTime(firstStart),
            endTime: toTime(firstEnd),
            description: 'أول جلسة بعد وقت الأذان'
          });
        } else {
          // log.info(`First slot would exceed boundary: ${toTime(firstStart)} - ${toTime(firstEnd)} (boundary: ${toTime(boundaryEnd)})`);
        }
      } else {
        // Gaps between sessions within boundaries
        for (let i = 0; i < existingSessions.length - 1; i++) {
          const currentEnd = toDate(existingSessions[i].sessionEndTime);
          const nextStart = toDate(existingSessions[i + 1].sessionStartTime);
          const candidateStart = new Date(Math.max(currentEnd.getTime(), boundaryStart.getTime()));
          const candidateEnd = new Date(candidateStart.getTime() + 30 * 60000);
          
          // Validate: slot must fit between sessions AND within prayer boundary
          if (candidateEnd <= nextStart && candidateEnd <= boundaryEnd) {
            availableSlots.push({
              startTime: toTime(candidateStart),
              endTime: toTime(candidateEnd),
              description: `بعد جلسة ${this.formatTime(existingSessions[i].sessionStartTime)}`
            });
          } else {
            // log.info(`Gap slot would exceed boundary or overlap: ${toTime(candidateStart)} - ${toTime(candidateEnd)} (next: ${toTime(nextStart)}, boundary: ${toTime(boundaryEnd)})`);
          }
        }
        
        // After the last session, still within prayer boundary
        const lastEnd = toDate(existingSessions[existingSessions.length - 1].sessionEndTime);
        const candidateStart = new Date(Math.max(lastEnd.getTime(), boundaryStart.getTime()));
        const candidateEnd = new Date(candidateStart.getTime() + 30 * 60000);
        
        // Validate: slot must fit within prayer boundary
        if (candidateEnd <= boundaryEnd) {
          availableSlots.push({
            startTime: toTime(candidateStart),
            endTime: toTime(candidateEnd),
            description: 'بعد آخر جلسة'
          });
        } else {
          // log.info(`Last slot would exceed boundary: ${toTime(candidateStart)} - ${toTime(candidateEnd)} (boundary: ${toTime(boundaryEnd)})`);
        }
        
        // Before the first session (if room after boundaryStart)
        const firstSessionStart = toDate(existingSessions[0].sessionStartTime);
        const earliestStart = new Date(boundaryStart.getTime());
        const earliestEnd = new Date(earliestStart.getTime() + 30 * 60000);
        
        // Validate: slot must fit before first session AND within prayer boundary
        if (earliestEnd <= firstSessionStart && earliestEnd <= boundaryEnd) {
          availableSlots.unshift({
            startTime: toTime(earliestStart),
            endTime: toTime(earliestEnd),
            description: 'أول جلسة بعد وقت الأذان'
          });
        } else {
          // log.info(`Earliest slot would exceed boundary or overlap: ${toTime(earliestStart)} - ${toTime(earliestEnd)} (first: ${toTime(firstSessionStart)}, boundary: ${toTime(boundaryEnd)})`);
        }
      }
      
      this.availableTimeSlots = availableSlots;
      // console.log('Available time slots (bounded)', {
      //   prayer: selectedAthan.prayerName,
      //   boundaryStart: toTime(boundaryStart),
      //   boundaryEnd: toTime(boundaryEnd),
      //   slots: availableSlots
      // });
    },
    
    /**
     * Helper method to add minutes to a time string
     */
    addMinutes(timeString, minutes) {
      const [hours, mins] = timeString.split(':').map(Number);
      const date = new Date(2000, 0, 1, hours, mins + minutes);
      return date.toTimeString().substring(0, 5);
    },

    // Student Management Methods
    async openStudentModal(session) {
      this.selectedSessionTime = session;
      this.showStudentModal = true;
      this.loadingStudents = true;
      this.loadingSlots = false; // سنقوم بتحميل الأوقات لكل طالب على حدة
      this.sessionStudents = [];
      this.availableSlots = []; // سنقوم بتخزين الأوقات لكل طالب

      try {
        // Load students first
        const studentsResponse = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}${url.get_session_time_students}${session.id}/students`,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );

        this.sessionStudents = studentsResponse.data.result.data.students || [];
        
        // Load available slots for each student individually
        await this.loadAvailableSlotsForStudents();
        
        this.loadingStudents = false;

      } catch (error) {
        console.error('Error loading students:', error);
        this.showError(error.response?.data?.result?.mesAr || 'خطأ في تحميل البيانات');
        this.loadingStudents = false;
      }
    },

    // New method to load available slots for each student
    async loadAvailableSlotsForStudents() {
      if (!this.sessionStudents || this.sessionStudents.length === 0) return;
      
      this.loadingSlots = true;
      
      try {
        // Load available slots for each student
        for (const student of this.sessionStudents) {
          const slotsResponse = await axios.get(
            `${process.env.VUE_APP_SERVICE_URL}${url.get_available_session_times_for_move}${this.selectedSessionTime.id}/available-for-move?studentId=${student.studentId}`,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          );
          
          // Store available slots for this specific student
          student.availableSlots = slotsResponse.data.result.data.availableSessionTimes || [];
        }
        
        this.loadingSlots = false;
        
      } catch (error) {
        console.error('Error loading available slots for students:', error);
        this.showError('خطأ في تحميل الأوقات المتاحة للطلاب');
        this.loadingSlots = false;
      }
    },

    closeStudentModal() {
      this.showStudentModal = false;
      this.selectedSessionTime = null;
      this.sessionStudents = [];
      this.availableSlots = [];
      this.loadingStudents = false;
      this.loadingSlots = false;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-SA');
    },

    async moveStudent(student, newSessionTimeId) {
      if (!newSessionTimeId) return;
      
      // Find the selected slot from this student's available slots
      const selectedSlot = student.availableSlots?.find(slot => slot.id == newSessionTimeId);
      if (!selectedSlot) return;
      
      const confirmed = confirm(
        `هل أنت متأكد من نقل الدارس "${student.studentName}" إلى:\n${this.getPrayerNameArabic(selectedSlot.prayerName)} ${this.formatTime(selectedSlot.sessionStartTime)}-${this.formatTime(selectedSlot.sessionEndTime)}؟`
      );
      
      if (!confirmed) {
        // Reset the select dropdown
        event.target.value = '';
        return;
      }

      try {
        await axios.post(
          `${process.env.VUE_APP_SERVICE_URL}${url.move_student_to_session}${student.studentId}/move-to-session/${newSessionTimeId}`,
          {},
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        
        this.showSuccess(`تم نقل الدارس ${student.studentName} بنجاح`);
        
        // Refresh the student list and session times
        await this.openStudentModal(this.selectedSessionTime);
        await this.loadSessionTimes();
        
      } catch (error) {
        console.error('Error moving student:', error);
        this.showError(error.response?.data?.result?.mesAr || 'خطأ في نقل الدارس');
        // Reset the select dropdown
        event.target.value = '';
      }
    },

    async manualPrayerTimeUpdate() {
      if (!confirm('هل أنت متأكد من تحديث أوقات الصلاة؟ هذا سيؤدي إلى تحديث جميع أوقات الصلاة في هذه المنطقة.')) {
        return;
      }

      this.updatingPrayerTimes = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_SERVICE_URL}${url.manual_prayer_time_update}`,
          {},
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        
        if (response.data?.result?.status === 200) {
          this.showSuccess('تم تحديث أوقات الصلاة بنجاح!');
          // Reload data to reflect new prayer times
          await this.loadAthanTimes();
          await this.loadSessionTimes();
        } else {
          throw new Error('Failed to update prayer times');
        }
      } catch (error) {
        console.error('Error updating prayer times:', error);
        this.showError(error.response?.data?.result?.mesAr || 'خطأ في تحديث أوقات الصلاة');
      } finally {
        this.updatingPrayerTimes = false;
      }
    },

    /**
     * Check if a slot can be moved up (has lower session_order)
     */
    canMoveUp(session) {
      if (!session.sessionOrder || session.sessionOrder <= 1) return false;
      
      // Check if there's a session with lower order in the same prayer
      const samePrayerSessions = this.sessionTimes.filter(s => 
        s.prayerName === session.prayerName
      );
      
      return samePrayerSessions.some(s => s.sessionOrder === session.sessionOrder - 1);
    },

    /**
     * Check if a slot can be moved down (has higher session_order)
     */
    canMoveDown(session) {
      if (!session.sessionOrder) return false;
      
      // Check if there's a session with higher order in the same prayer
      const samePrayerSessions = this.sessionTimes.filter(s => 
        s.prayerName === session.prayerName
      );
      
      return samePrayerSessions.some(s => s.sessionOrder === session.sessionOrder + 1);
    },

    /**
     * Move a slot up (decrease session_order)
     */
    async moveSlotUp(session) {
      if (!this.canMoveUp(session)) return;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_SERVICE_URL}${url.reorder_session_slot}`,
          {
            sessionId: session.id,
            direction: 'up'
          },
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        
        if (response.data?.result?.status === 200) {
          this.showSuccess('تم نقل الجلسة بنجاح!');
          await this.loadSessionTimes(); // Reload to show new order
        } else {
          throw new Error('Failed to reorder slot');
        }
      } catch (error) {
        console.error('Error reordering slot:', error);
        this.showError(error.response?.data?.result?.mesAr || 'خطأ في نقل الجلسة');
      }
    },

    /**
     * Move a slot down (increase session_order)
     */
    async moveSlotDown(session) {
      if (!this.canMoveDown(session)) return;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_SERVICE_URL}${url.reorder_session_slot}`,
          {
            sessionId: session.id,
            direction: 'down'
          },
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        
        if (response.data?.result?.status === 200) {
          this.showSuccess('تم نقل الجلسة بنجاح!');
          await this.loadSessionTimes(); // Reload to show new order
        } else {
          throw new Error('Failed to reorder slot');
        }
      } catch (error) {
        console.error('Error reordering slot:', error);
        this.showError(error.response?.data?.result?.mesAr || 'خطأ في نقل الجلسة');
      }
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar for table overflow */
.overflow-x-auto {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f3f4f6;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Responsive table adjustments */
@media (max-width: 768px) {
  table {
    font-size: 0.875rem;
  }
  
  td, th {
    @apply px-2 py-2;
  }
  
  /* Stack header elements on mobile */
  .flex.justify-between.items-center {
    @apply flex-col gap-4 items-stretch;
  }
  
  .flex.justify-between.items-center h2 {
    @apply text-xl text-center;
  }
  
  .flex.justify-between.items-center button {
    @apply w-full;
  }
}

/* Ensure table cells don't break unnecessarily */
td, th {
  white-space: nowrap;
}

/* Allow action buttons to wrap if needed */
td:last-child {
  white-space: normal;
}

/* Ensure proper spacing for the main content */
main {
  margin-top: 1rem;
}
</style>
